import React from 'react';
import './Table.css';

export interface TableColumn<T> {
  header: string;
  accessor: keyof T | ((row: T) => React.ReactNode);
  render?: (value: unknown, row: T) => React.ReactNode;
  dataLabel?: string;
  hideOnMobile?: boolean;
  hideOnTablet?: boolean;
  minWidth?: string;
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  getRowId: (row: T) => string;
  emptyMessage?: string;
  className?: string;
  containerClassName?: string;
}

export function Table<T>({
  columns,
  data,
  getRowId,
  emptyMessage = 'No data found',
  className = '',
  containerClassName = '',
}: TableProps<T>) {
  const renderCell = (column: TableColumn<T>, row: T) => {
    if (typeof column.accessor === 'function') {
      return column.accessor(row);
    }

    const value = row[column.accessor];
    
    if (column.render) {
      return column.render(value, row);
    }

    return value as React.ReactNode;
  };

  return (
    <div className={`table-container ${containerClassName}`}>
      <table className={`data-table ${className}`}>
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th
                key={index}
                className={`${column.hideOnMobile ? 'hide-on-mobile' : ''} ${column.hideOnTablet ? 'hide-on-tablet' : ''}`}
                style={column.minWidth ? { minWidth: column.minWidth } : undefined}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="empty-state">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row) => (
              <tr key={getRowId(row)}>
                {columns.map((column, index) => {
                  const dataLabel = column.dataLabel || column.header;
                  return (
                    <td
                      key={index}
                      data-label={dataLabel}
                      className={`${column.hideOnMobile ? 'hide-on-mobile' : ''} ${column.hideOnTablet ? 'hide-on-tablet' : ''}`}
                      style={column.minWidth ? { minWidth: column.minWidth } : undefined}
                    >
                      {renderCell(column, row)}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

